import React, { useState, useEffect, useRef } from 'react';
import Swiper from 'swiper';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import { getTours, getItineraryDownloadUrl } from '../../services/tourService';

// Skeleton Loader Component
const TourSkeleton = () => (
  <div className="swiper-slide">
    <div className="tour-box bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
      <div className="w-full h-48 bg-gray-300"></div>
      <div className="p-4 space-y-3">
        <div className="h-6 bg-gray-300 rounded w-3/4"></div>
        <div className="flex justify-between">
          <div className="h-4 bg-gray-300 rounded w-1/3"></div>
          <div className="h-4 bg-gray-300 rounded w-1/4"></div>
        </div>
        <div className="h-4 bg-gray-300 rounded w-full"></div>
        <div className="h-4 bg-gray-300 rounded w-2/3"></div>
        <div className="flex justify-between items-center">
          <div className="h-6 bg-gray-300 rounded w-1/4"></div>
          <div className="h-8 bg-gray-300 rounded w-1/3"></div>
        </div>
      </div>
    </div>
  </div>
);

// Lazy Load Image Component with Placeholder
const LazyImage = ({ src, alt, className }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
    setImageSrc(null);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setImageSrc(src);
              observer.disconnect();
            }
          });
        },
        { rootMargin: '50px', threshold: 0.01 }
      );

      if (imgRef.current) observer.observe(imgRef.current);
      return () => observer.disconnect();
    } else {
      setImageSrc(src);
    }
  }, [src]);

  return (
    <div ref={imgRef} className="relative w-full h-48 bg-gray-200 overflow-hidden">
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-[#153184] rounded-full animate-spin"></div>
            <span className="text-xs text-gray-400">Loading...</span>
          </div>
        </div>
      )}

      {imageSrc && !hasError && (
        <img
          src={imageSrc}
          alt={alt}
          className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500 ease-in-out`}
          loading="lazy"
          width="300"
          height="200"
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            setHasError(true);
            setIsLoaded(true);
          }}
        />
      )}

      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <div className="text-center p-4">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm text-gray-500">Image unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
};

const Tours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleTours, setVisibleTours] = useState(8);
  const [downloadingId, setDownloadingId] = useState(null);
  const swiperRef = useRef(null);
  const swiperInstance = useRef(null);

  // Fetch tours
  useEffect(() => {
    const fetchTours = async () => {
      try {
        const toursData = await getTours();
        setTours(Array.isArray(toursData) ? toursData : []);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, []);

  // Initialize / destroy Swiper whenever tours or visible count changes
  useEffect(() => {
    if (loading || tours.length === 0) return;

    if (swiperInstance.current) {
      swiperInstance.current.destroy(true, true);
      swiperInstance.current = null;
    }

    const timer = setTimeout(() => {
      if (swiperRef.current) {
        swiperInstance.current = new Swiper(swiperRef.current, {
          modules: [Autoplay],
          breakpoints: {
            0: { slidesPerView: 1 },
            576: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            992: { slidesPerView: 2 },
            1200: { slidesPerView: 3 },
            1300: { slidesPerView: 4 },
          },
          spaceBetween: 30,
          loop: tours.length > 4,
          autoplay: {
            delay: 4000,
            disableOnInteraction: false,
          },
        });
      }
    }, 0);

    return () => {
      clearTimeout(timer);
      if (swiperInstance.current) {
        swiperInstance.current.destroy(true, true);
        swiperInstance.current = null;
      }
    };
  }, [tours, loading, visibleTours]);

  // Download the PDF in-place. Works from any origin.
  const handleDownload = async (rawFileName, title, tourId) => {
    if (!rawFileName) return;
    setDownloadingId(tourId);

    const safeName = `${title.replace(/\s+/g, '-').toLowerCase()}-itinerary.pdf`;

    try {
      // 1. Resolve filename -> token-signed download URL via Firebase SDK
      const url = await getItineraryDownloadUrl(rawFileName);
      if (!url) throw new Error('No download URL available');

      // 2. Fetch the file as a blob (so we never navigate to Firebase)
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} — ${response.statusText}`);
      }

      const blob = await response.blob();

      // 3. Save As picker (Chrome/Edge desktop)
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: safeName,
            types: [
              {
                description: 'PDF Document',
                accept: { 'application/pdf': ['.pdf'] },
              },
            ],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return; // user cancelled
          // else fall through to anchor download
        }
      }

      // 4. Fallback: hidden anchor pointing at a local blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = safeName;
      link.rel = 'noopener';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Could not download the itinerary. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  // Skeleton state
  if (loading) {
    const skeletonCount = 8;
    return (
      <section id="tour-sec" className="tour-area position-relative bg-top-center overflow-hidden space">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 offset-lg-3">
              <div className="title-area text-center">
                <span className="sub-title">Best Place For You</span>
                <h2 className="sec-title">Our Popular Tour</h2>
              </div>
            </div>
          </div>
          <div className="slider-area tour-slider">
            <div className="swiper th-slider has-shadow slider-drag-wrap">
              <div className="swiper-wrapper">
                {[...Array(skeletonCount)].map((_, index) => (
                  <TourSkeleton key={index} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (tours.length === 0) {
    return (
      <section id="tour-sec" className="tour-area position-relative bg-top-center overflow-hidden space">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 offset-lg-3">
              <div className="title-area text-center">
                <span className="sub-title">Best Place For You</span>
                <h2 className="sec-title">Our Popular Tour</h2>
              </div>
            </div>
          </div>
          <div className="text-center py-12">
            <p className="text-gray-500">No tours available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  const displayTours = tours.slice(0, visibleTours);

  return (
    <section id="tour-sec" className="tour-area position-relative bg-top-center overflow-hidden space">
      <div className="container">
        <div className="row">
          <div className="col-lg-6 offset-lg-3">
            <div className="title-area text-center">
              <span className="sub-title">Best Place For You</span>
              <h2 className="sec-title">Our Popular Tour</h2>
            </div>
          </div>
        </div>

        <div className="slider-area tour-slider">
          <div className="swiper th-slider has-shadow slider-drag-wrap" ref={swiperRef}>
            <div className="swiper-wrapper">
              {displayTours.map((tour) => {
                const hasItinerary = Boolean(tour.itineraryPdf);
                const isDownloading = downloadingId === tour.id;

                return (
                  <div className="swiper-slide" key={tour.id}>
                    <div className="tour-box th-ani gsap-cursor bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                      <div className="tour-box_img global-img overflow-hidden rounded-t-lg">
                        <LazyImage
                          src={tour.coverImage || '/assets/images/placeholder.jpg'}
                          alt={tour.title}
                          className="w-full h-48 object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>

                      <div className="tour-content flex flex-col h-full p-4">
                        <h3 className="box-title text-lg font-semibold mb-2 line-clamp-1">
                          {tour.title}
                        </h3>

                        {/* Rating and Duration */}
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <svg
                                  key={i}
                                  className={`w-4 h-4 ${i < Math.floor(tour.rating || 4) ? 'text-yellow-400' : 'text-gray-300'}`}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            <span className="text-sm text-gray-600">({tour.rating || 4.8})</span>
                          </div>
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {tour.duration || 'N/A'}
                          </span>
                        </div>

                        {/* Description */}
                        {tour.description && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2 flex-grow">
                            {tour.description.length > 100
                              ? `${tour.description.substring(0, 100)}...`
                              : tour.description}
                          </p>
                        )}

                        {/* Price and Download */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {/* <span className="text-xl font-bold text-[#153184]">N${tour.price}</span> */}
                              {tour.featured && (
                                <span className="bg-[#ff6b35] text-white text-xs px-2 py-1 rounded-full">
                                  Featured
                                </span>
                              )}
                            </div>
                          </div>

                          {hasItinerary ? (
                            <button
                              type="button"
                              disabled={isDownloading}
                              onClick={() =>
                                handleDownload(tour.itineraryPdf, tour.title, tour.id)
                              }
                              className="flex items-center justify-center gap-2 w-full bg-[#153184] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#1f3a95] transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {isDownloading ? (
                                <>
                                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                  </svg>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  Download Itinerary
                                </>
                              )}
                            </button>
                          ) : (
                            <div className="w-full bg-gray-100 text-gray-400 px-4 py-2 rounded-lg text-sm text-center">
                              No itinerary available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Load More */}
        {tours.length > visibleTours && (
          <div className="text-center mt-6">
            <button
              onClick={() => setVisibleTours((prev) => prev + 8)}
              className="bg-[#153184] text-white px-6 py-2 rounded-lg hover:bg-[#1f3a95] transition-all duration-300"
            >
              Load More Tours ({tours.length - visibleTours} remaining)
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Tours;